// SP3-full slice 3 (D5) — the asset canvas's 4-state provenance resolution.
//
// The boat canvas's useProvenance reads react-hook-form `_provenance.*` state;
// the asset canvas holds a plain provenance map instead, so the tint logic
// lives here as pure functions over the SAME ProvenanceEntry shape and the
// SAME canvas.css classes:
//
//   tint-ai       violet — AI-filled, high confidence (≥ AI_CONFIDENCE_FLOOR
//                 / merge state auto_applied)
//   tint-ai-low   amber "please check" — low confidence / merge suggested
//   tint-conflict amber 2px — sources disagree (array entry). The asset
//                 canvas ships no ConflictChooser; typing a value resolves
//                 (manual-edit-wins).
//   ''            neutral — user-edited / manual / no provenance.

import { AI_CONFIDENCE_FLOOR } from "../canvas/hooks/useProvenance";
import type { ProvenanceEntry, ProvenanceSource } from "@/modules/inventory/types/boat.types";

export type AssetProvenanceMap = Record<string, ProvenanceEntry | ProvenanceEntry[]>;

export type AssetTint = "tint-ai" | "tint-ai-low" | "tint-conflict" | "";

export function assetTintClass(entry: ProvenanceEntry | ProvenanceEntry[] | undefined | null): AssetTint {
  if (!entry) return "";

  if (Array.isArray(entry)) {
    if (entry.length === 0) return "";
    // A user-edited or picked (#1317 confirmed) entry inside a conflict means the dealer already chose.
    return entry.some((e) => e.userEdited || e.state === "confirmed") ? "" : "tint-conflict";
  }

  if (entry.userEdited || entry.source === "manual") return "";
  // Merge-authoritative states (field_sources hydration) win over the
  // confidence heuristic; stream-only entries fall through to the floor.
  if (entry.state === "suggested") return "tint-ai-low";
  if (entry.state === "edited" || entry.state === "rejected") return "";
  return entry.confidence >= AI_CONFIDENCE_FLOOR ? "tint-ai" : "tint-ai-low";
}

/** Drives ProvenanceChip's amber "please check" variant. */
export function isLowConfidenceAssetEntry(entry: ProvenanceEntry | ProvenanceEntry[] | undefined | null): boolean {
  return !!entry && !Array.isArray(entry) && assetTintClass(entry) === "tint-ai-low";
}

/**
 * Map a streamed field event's source descriptor onto the canonical
 * ProvenanceSource union (the useFactoryPacketFill rules).
 */
export function toAssetProvenanceSource(
  source: { doc_type?: string; source_type?: string } | null | undefined,
): ProvenanceSource {
  if (source?.source_type === "AI-photo") return "AI-photo";
  switch (source?.doc_type) {
    case "coo_boat":
      return "COO-boat";
    case "coo_trailer":
      return "COO-trailer";
    case "bill_of_lading":
      return "BOL";
    case "invoice":
      return "invoice";
    default:
      return "PDF";
  }
}
